import { StateCreator } from 'zustand'

export type ThemeMode = 'light' | 'dark'

const THEME_KEY = 'tdc_theme'

// Anything other than the literal string 'dark' (missing key, malformed
// value, storage unavailable) resolves to light.
const readStoredMode = (): ThemeMode => {
  try {
    if (typeof window === 'undefined') return 'light'
    return window.localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

export interface ThemeSlice {
  /** The user's saved preference. */
  themeMode: ThemeMode
  /**
   * Theme ELIGIBILITY, separate from preference: a ref-count of mounted
   * layouts that permit dark mode (AppShell today; any future authenticated
   * layout can also declare itself via useDarkThemeScope). Public, login,
   * share and RMN white-label routes never enter a scope, so they always
   * render light regardless of the saved preference.
   */
  darkScopes: number
  setThemeMode: (mode: ThemeMode) => void
  enterDarkScope: () => void
  exitDarkScope: () => void
}

// Effective theme = preference + eligibility. ThemeRoot is the ONLY writer of
// the document's data-theme attribute, computed from this.
export const getEffectiveThemeMode = (
  s: Pick<ThemeSlice, 'themeMode' | 'darkScopes'>
): ThemeMode => (s.darkScopes > 0 && s.themeMode === 'dark' ? 'dark' : 'light')

export const createThemeSlice: StateCreator<ThemeSlice, [], [], ThemeSlice> = (set) => ({
  themeMode: readStoredMode(),
  darkScopes: 0,
  setThemeMode: (mode) => {
    set({ themeMode: mode })
    try {
      window.localStorage.setItem(THEME_KEY, mode)
    } catch {
      /* ignore */
    }
  },
  enterDarkScope: () => set((s) => ({ darkScopes: s.darkScopes + 1 })),
  exitDarkScope: () => set((s) => ({ darkScopes: Math.max(0, s.darkScopes - 1) })),
})
